"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.isAllegroExpandedNetlist = isAllegroExpandedNetlist;
exports.parseAllegroNetlist = parseAllegroNetlist;
const sigma_engine_shared_1 = require("@cadstrom/sigma-engine-shared");
const AllegroTypes_1 = require("./AllegroTypes");
const EXPANDED_NETLIST_HEADER = 'FILE_TYPE = EXPANDEDNETLIST';
function isAllegroExpandedNetlist(content) {
    const firstLine = content.split(/\r?\n/, 1)[0].trim();
    return firstLine.startsWith(EXPANDED_NETLIST_HEADER);
}
function parseAllegroNetlist(content) {
    const warnings = [];
    const lines = content.split(/\r?\n/);
    const netlist = {
        nets: [],
    };
    let i = 0;
    if (i < lines.length) {
        const headerLine = lines[i].trim();
        if (!headerLine.startsWith(EXPANDED_NETLIST_HEADER)) {
            warnings.push({
                type: AllegroTypes_1.AllegroParsingMessageType.ParsingWarning,
                message: `Expected FILE_TYPE = EXPANDEDNETLIST header, found: ${headerLine}`,
                severity: sigma_engine_shared_1.BoundaryLevel.Warning,
            });
        }
        i++;
    }
    if (i < lines.length) {
        const commentLine = lines[i].trim();
        if (commentLine.startsWith('{') && commentLine.endsWith('}')) {
            netlist.headerComment = commentLine.slice(1, -1).trim();
            i++;
        }
    }
    let currentNet;
    while (i < lines.length) {
        const line = lines[i].trim();
        if (!line || line === 'END.') {
            i++;
            continue;
        }
        if (line === 'NET_NAME') {
            currentNet = parseNetNameBlock(lines, i, warnings);
            if (currentNet) {
                netlist.nets.push(currentNet);
            }
            i++;
            i = collectNetProperties(lines, i, currentNet, warnings);
            continue;
        }
        if (line.startsWith('NODE_NAME')) {
            const node = parseNodeNameBlock(lines, i, warnings);
            if (node && currentNet) {
                currentNet.nodes.push(node);
            }
            else if (node && !currentNet) {
                warnings.push({
                    type: AllegroTypes_1.AllegroParsingMessageType.ParsingWarning,
                    message: `NODE_NAME "${node.refdes} ${node.pin}" found outside a NET_NAME block`,
                    severity: sigma_engine_shared_1.BoundaryLevel.Warning,
                });
            }
            i++;
            i = collectNodeProperties(lines, i, node, warnings);
            continue;
        }
        i++;
    }
    const processingResults = {
        type: 'allegro-parsing',
        message: warnings.length > 0 ?
            `Allegro netlist parsing completed with ${warnings.length} warning(s)`
            : 'Allegro netlist parsing completed',
        innerResults: warnings,
        severity: warnings.length > 0 ? sigma_engine_shared_1.BoundaryLevel.Warning : sigma_engine_shared_1.BoundaryLevel.Recommendation,
    };
    return { netlist, processingResults };
}
function parseNetNameBlock(lines, _i, warnings) {
    return {
        name: '',
        properties: {},
        nodes: [],
    };
}
function collectNetProperties(lines, i, net, warnings) {
    if (!net) {
        return i;
    }
    let gotName = false;
    let propertyBuffer = '';
    while (i < lines.length) {
        const line = lines[i].trim();
        if (!line) {
            i++;
            continue;
        }
        if (line === 'NET_NAME' || line.startsWith('NODE_NAME') || line === 'END.') {
            break;
        }
        if (!gotName) {
            net.name = stripQuotes(line);
            gotName = true;
            i++;
            continue;
        }
        if (!net.instancePath && line.startsWith("'@")) {
            net.instancePath = stripQuotes(line.replace(/:$/, ''));
            i++;
            continue;
        }
        propertyBuffer += ' ' + line;
        if (line.endsWith(';')) {
            parsePropertyString(propertyBuffer, net.properties);
            propertyBuffer = '';
        }
        i++;
    }
    if (propertyBuffer.trim()) {
        parsePropertyString(propertyBuffer, net.properties);
    }
    if (net.properties['PHYS_NET_NAME']) {
        net.name = net.properties['PHYS_NET_NAME'];
    }
    return i;
}
function parseNodeNameBlock(lines, i, warnings) {
    const line = lines[i].trim();
    const match = line.match(/^NODE_NAME\s+(\S+)\s+(\S+)$/);
    if (!match) {
        warnings.push({
            type: AllegroTypes_1.AllegroParsingMessageType.ParsingWarning,
            message: `Invalid NODE_NAME line: ${line}`,
            severity: sigma_engine_shared_1.BoundaryLevel.Warning,
        });
        return undefined;
    }
    return {
        refdes: match[1],
        pin: match[2],
        properties: {},
    };
}
function collectNodeProperties(lines, i, node, warnings) {
    if (!node) {
        return i;
    }
    let propertyBuffer = '';
    while (i < lines.length) {
        const line = lines[i].trim();
        if (!line) {
            i++;
            continue;
        }
        if (line === 'NET_NAME' || line.startsWith('NODE_NAME') || line === 'END.') {
            break;
        }
        if (!node.instancePath && line.startsWith("'@")) {
            node.instancePath = stripQuotes(line.replace(/:$/, ''));
            i++;
            continue;
        }
        propertyBuffer += ' ' + line;
        if (line.endsWith(';')) {
            parseNodePropertyString(propertyBuffer, node);
            propertyBuffer = '';
        }
        i++;
    }
    if (propertyBuffer.trim()) {
        parseNodePropertyString(propertyBuffer, node);
    }
    return i;
}
function parsePropertyString(raw, target) {
    const cleaned = raw.trim().replace(/;$/, '');
    const pairs = splitProperties(cleaned);
    for (const pair of pairs) {
        const trimmed = pair.trim();
        if (!trimmed) {
            continue;
        }
        const eqIndex = trimmed.indexOf('=');
        if (eqIndex === -1) {
            continue;
        }
        const key = trimmed.substring(0, eqIndex).trim();
        const value = stripQuotes(trimmed.substring(eqIndex + 1).trim());
        target[key] = value;
    }
}
function parseNodePropertyString(raw, node) {
    const cleaned = raw.trim().replace(/;$/, '');
    const pinNameMatch = cleaned.match(/^'([^']*)':\s*(.*)/s);
    if (pinNameMatch) {
        node.pinName = pinNameMatch[1];
        parsePropertyString(pinNameMatch[2], node.properties);
    }
    else {
        parsePropertyString(cleaned, node.properties);
    }
}
function splitProperties(str) {
    const results = [];
    let current = '';
    let inQuote = false;
    for (let i = 0; i < str.length; i++) {
        const ch = str[i];
        if (ch === "'") {
            inQuote = !inQuote;
        }
        if (ch === ',' && !inQuote) {
            results.push(current);
            current = '';
        }
        else {
            current += ch;
        }
    }
    if (current.trim()) {
        results.push(current);
    }
    return results;
}
function stripQuotes(str) {
    const trimmed = str.trim();
    if (trimmed.startsWith("'") && trimmed.endsWith("'")) {
        return trimmed.slice(1, -1);
    }
    return trimmed;
}
//# sourceMappingURL=AllegroNetlistParser.js.map